import { getOwner } from '@ember/owner';
import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import { type Task, task } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import type { Period } from 'lumen/models';
import type { RouteQueryParams as MarketParityInsightParams } from 'lumen/routes/market/parity/insight';
import type { Transition } from 'lumen/services/transition';
import {
  APIGATEWAY,
  MEMBERSHIPTYPES,
} from 'lumen/utils/constants/general-constants';
import ExportConfigResource from 'lumen/utils/parity/resources/export-config';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type RouteQueryParams = {
  exportDate: string | null;
  hotelId: string;
  platform: Platform;
};

export type ExportDateObject = {
  extract_date: string;
  from_dates: string[];
  los: number;
  persons: number;
};

type ParityPermissionResponse = {
  has_permission: boolean;
  hotel_id: string;
};

export type HistoricalRouteModel = {
  exports: ExportDateObject[];
  hotel: Hotel;
  parities: Task<Period[], [RouteQueryParams, Hotel, ExportDateObject[]]>;
  parityPermission: Task<ParityPermissionResponse, [string]>;
};

type ExportDateCache = {
  [hotelId: string]: {
    [extractDate: string]: {
      [dateAggregation: string]: {
        [fromDateType: number]: {
          [platform: number]: ExportDateObject[];
        };
      };
    };
  };
};

export default class MarketParityInsightHistoricalRoute extends Route {
  queryParams = {
    hotelId: { refreshModel: true },
    exportDate: { refreshModel: true },
    platform: { refreshModel: true },
  };

  @service declare ajax: Services['ajax'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  exportConfigResource = new ExportConfigResource(getOwner(this)!);
  exportDateCache = trackedObject<ExportDateCache>({});

  fetchExportDatesTask = task(
    { restartable: true },
    async (params: RouteQueryParams) => {
      try {
        const { hotelId, platform } = params;
        const { extractDate, dateAggregation, fromDateType } = this.paramsFor(
          'market.parity.insight',
        ) as MarketParityInsightParams;

        const cachedExportDates =
          this.exportDateCache[hotelId]?.[extractDate]?.[dateAggregation]?.[
            fromDateType
          ]?.[platform];

        if (cachedExportDates) {
          return cachedExportDates;
        }

        const apiParams = {
          subscription_id: hotelId,
          extract_date: extractDate,
          date_aggregation: dateAggregation,
          from_date_type: fromDateType,
        };

        const exportConfigResponse =
          await this.exportConfigResource.fetchResourceTask.perform(apiParams);

        const exportDates: ExportDateObject[] = exportConfigResponse.flatMap(
          (exportConfigsPerOtaPerPlatform) => {
            const exportConfigsPerOta =
              exportConfigsPerOtaPerPlatform[`${platform}`];
            if (!exportConfigsPerOta) {
              return [];
            }

            const exportConfigs = Object.values(exportConfigsPerOta);
            const allFromDates = new Set<string>();
            exportConfigs.forEach((ota) =>
              ota.from_dates.forEach((date) => allFromDates.add(date)),
            );

            // The platform has no OTAs, or no from_dates for the selected from date type
            if (!allFromDates.size) {
              return [];
            }

            const firstExportConfig = exportConfigs[0]!;

            return [
              {
                extract_date: firstExportConfig.extract_date,
                from_dates: [...allFromDates],
                los: firstExportConfig.los,
                persons: firstExportConfig.persons,
              },
            ];
          },
        );

        const sortedExportDates = exportDates.sort(
          (a, b) => dayjs(a.extract_date).unix() - dayjs(b.extract_date).unix(),
        );

        this.exportDateCache[hotelId] ??= {};
        this.exportDateCache[hotelId][extractDate] ??= {};
        this.exportDateCache[hotelId][extractDate][dateAggregation] ??= {};
        this.exportDateCache[hotelId][extractDate][dateAggregation][
          fromDateType
        ] ??= {};
        this.exportDateCache[hotelId][extractDate][dateAggregation][
          fromDateType
        ][platform] = sortedExportDates;

        return sortedExportDates;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchParityPermissionTask = task(
    { restartable: true },
    async (hotelId: string) => {
      try {
        return await this.ajax.request<ParityPermissionResponse>(
          `${this.ajax.host}/api/v3/parity/checkpermission/?hotel_id=${hotelId}`,
        );
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchPeriodsWithParitiesTask = task(
    { restartable: true },
    async (
      params: RouteQueryParams,
      hotel: Hotel,
      sortedExportDates: ExportDateObject[],
    ) => {
      try {
        const exportDateObject = this.getExportDateObject(
          sortedExportDates,
          params.exportDate,
        );

        const { los, persons, from_dates: fromDates } = exportDateObject;
        const { mealPlan, pos, rateType, showMemberRate } = this.paramsFor(
          'market.parity.insight',
        ) as MarketParityInsightParams;

        const fromDateDays = fromDates.map((date) => dayjs(date));
        const minFromDate = dayjs.min(fromDateDays)!.format('YYYY-MM-DD');
        const maxFromDate = dayjs.max(fromDateDays)!.format('YYYY-MM-DD');

        const requestParams = {
          namespace: APIGATEWAY,
          typeKey: 'parities/analytics',
          persons: persons,
          platform: params.platform,
          // Filter on ota until BE stops returning Platform unspecified (RATE-888)
          ota: hotel
            .getParityAnalyticsOTAs(params.platform)
            .map((ota) => ota.id),
          los,
          pos,
          mealtype: mealPlan,
          rate_type: rateType,
          membershiptype: 0,
          extract_date: params.exportDate,
          from_date_range_start: minFromDate,
          from_date_range_end: maxFromDate,
          filter_meta_allow_major: true,
          group_meta_by: ['ota', 'channel'],
          hotel_id: hotel.id,
          meta: 'nested',
          base_membershiptype: showMemberRate
            ? MEMBERSHIPTYPES.MEMBER_ONLY
            : MEMBERSHIPTYPES.PUBLIC,
        };

        const periods = await this.store.query<Period>('period', requestParams);
        // Filter out gaps in from_dates (https://lighthouseintelligence.atlassian.net/browse/PI-1155?focusedCommentId=287741)
        return [...periods].filter((period) =>
          fromDates.includes(period.day.formattedDate),
        );
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  attemptTransition(transition: Transition, params: RouteQueryParams): void {
    if (!transition.isAborted) {
      transition.abort();
      this.router.transitionTo(
        'market.parity.insight.historical',
        this.hotelAndMarketService.market,
        { queryParams: params },
      );
    }
  }

  getExportDateObject(
    exportDateObjects: ExportDateObject[],
    currentExportDate: string | null,
  ): ExportDateObject {
    return exportDateObjects.find(
      (exportDateObject) => exportDateObject.extract_date === currentExportDate,
    )!;
  }

  async model(
    params: RouteQueryParams,
    transition: Transition,
  ): Promise<HistoricalRouteModel> {
    const hotel =
      this.store.peekRecord<Hotel>('hotel', params.hotelId) ??
      (await this.store.findRecord<Hotel>('hotel', params.hotelId, {
        reload: false,
      }));

    const model: HistoricalRouteModel = {
      hotel,
      parities: this.fetchPeriodsWithParitiesTask,
      parityPermission: this.fetchParityPermissionTask,
      exports: [] as ExportDateObject[],
    };

    void model.parityPermission.perform(params.hotelId);

    const sortedExportDates = await this.fetchExportDatesTask.perform(params);
    model.exports = sortedExportDates;

    if (!sortedExportDates.length) {
      return model;
    }

    if (
      !params.exportDate ||
      !sortedExportDates
        .map((date) => date.extract_date)
        .includes(params.exportDate)
    ) {
      // This makes sure we transition to have the export date SET (not null)
      params.exportDate = sortedExportDates.at(-1)!.extract_date;
      this.attemptTransition(transition, params);
    } else {
      void model.parities.perform(params, hotel, sortedExportDates);
    }

    return model;
  }
}
